/* Drop One Word ✦ — emotional atmosphere from the room.
 *
 * Four screens (mobile-first, editorial walnut/ivory/gold):
 *   1. INTRO        — "How does tonight feel ✦"
 *   2. INPUT        — single line + character counter + suggestion pills
 *   3. CONFIRMATION — "Your feeling now lives inside tonight ✦"
 *   4. WALL         — cinematic floating typography (frequency-weighted)
 *
 * Performance contract (locked):
 *   • CSS transforms only — no canvas, no physics engine.
 *   • Capped at 18 visible words on screen at any time.
 *   • Spaced via a coarse 6-column grid with intentional gutters
 *     so words never overlap, even at the smallest mobile width.
 *   • requestAnimationFrame is NOT used — pure CSS keyframe drift.
 *
 * Why a fixed visible cap of 18?
 *   The room reads as one breathing atmosphere when there are
 *   ~12–18 anchored words on screen. More and it becomes a tag
 *   cloud; fewer and it reads as a list. We rotate which 18 are
 *   visible on each wall poll so a 2,000-submission night still
 *   feels alive without a 2,000-DOM-node performance hit.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import DropOneWordDownload from '../../components/DropOneWordDownload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Per-browser guest session token used by Drop One Word ✦ to coalesce
 * accidental double-taps without ever conflating two different
 * guests on the same venue Wi-Fi. Lives in localStorage so it
 * survives page reloads — same guest editing their feeling lands in
 * the same row across navigation.
 *
 * Iter171 fix: replaced server-side IP-based coalescing (which
 * silently overwrote every guest's word because they all shared the
 * venue's NAT'd IP). Per-browser token is the correct dedupe key.
 */
function getGuestSessionToken() {
  if (typeof window === 'undefined') return null;
  const KEY = 'eventdrop_guest_session_v1';
  try {
    let t = window.localStorage.getItem(KEY);
    if (!t) {
      // Build a UUID-ish string without depending on crypto.randomUUID
      // (Safari < 15.4 doesn't have it on http origins).
      t = 'g_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 12);
      window.localStorage.setItem(KEY, t);
    }
    return t;
  } catch {
    // Private mode / storage disabled — safe fallback: no token sent,
    // server treats every submission as a brand-new insert (no
    // coalescing). Worst case: an accidental double-tap creates two
    // rows. Acceptable degradation.
    return null;
  }
}

const INK = '#0c0805';
const INK_2 = '#1a120a';
const IVORY = '#f5efdf';
const IVORY_SOFT = 'rgba(245,239,223,0.74)';
const GOLD = '#c19a4b';
const HAIRLINE = 'rgba(245,239,223,0.16)';

const MAX_CHARS = 32;
const MAX_VISIBLE_WORDS = 18;
const FONT_DISPLAY = "'Cormorant Garamond', 'Playfair Display', Georgia, serif";
const FONT_BODY = "'Inter', system-ui, sans-serif";

const SUGGESTIONS = ['electric', 'forever', 'home', 'unreal', 'magic', 'loud', 'family', 'finally'];

const Shell = ({ children, dataState }) => (
  <div
    data-testid="one-word-page"
    data-state={dataState}
    style={{
      position: 'fixed', inset: 0,
      background: `radial-gradient(ellipse at 50% 35%, ${INK_2} 0%, ${INK} 70%)`,
      color: IVORY, fontFamily: FONT_BODY, overflow: 'hidden',
    }}
  >{children}</div>
);

const PoweredFooter = () => (
  <p
    data-testid="one-word-footer"
    style={{
      position: 'absolute', bottom: 'calc(env(safe-area-inset-bottom, 14px) + 14px)',
      left: 0, right: 0, textAlign: 'center', fontSize: 10.5,
      color: 'rgba(245,239,223,0.32)', letterSpacing: '0.16em',
    }}
  >Powered quietly by EventDrop ✦</p>
);

// ── 1. INTRO ────────────────────────────────────────────────
function IntroScreen({ onStart, onCancel }) {
  return (
    <Shell dataState="intro">
      <div style={{ padding: '32px 24px', maxWidth: 480, margin: '0 auto', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <button onClick={onCancel} data-testid="one-word-back" aria-label="Back" style={{ alignSelf: 'flex-start', background: 'transparent', border: 'none', color: IVORY_SOFT, fontSize: 18, cursor: 'pointer', padding: 0 }}>‹</button>
        <div style={{ marginTop: 'auto', marginBottom: 'auto' }}>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD }}>✦ Tonight</p>
          <h1 data-testid="one-word-title" style={{ fontFamily: FONT_DISPLAY, fontSize: 48, lineHeight: 1.05, margin: '16px 0 0', color: IVORY }}>
            Drop One Word ✦
          </h1>
          <p style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontStyle: 'italic', color: GOLD, marginTop: 18, marginBottom: 0 }}>
            How does tonight feel ✦
          </p>
          <p style={{ marginTop: 18, color: IVORY_SOFT, fontSize: 15, lineHeight: 1.65 }}>
            Leave one word or one feeling behind for the room.
          </p>
        </div>
        <button
          onClick={onStart}
          data-testid="one-word-start"
          style={{
            width: '100%', padding: '16px 18px', borderRadius: 999, border: 'none',
            cursor: 'pointer', background: `linear-gradient(180deg, ${GOLD}, #9a7836)`,
            color: '#1a0f04', fontWeight: 700, letterSpacing: '0.04em', fontSize: 15,
          }}
        >Leave My Word ✦</button>
      </div>
      <PoweredFooter />
    </Shell>
  );
}

// ── 2. INPUT ────────────────────────────────────────────────
function InputScreen({ onSubmit, onBack, busy, error }) {
  const [word, setWord] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 200); }, []);

  const remaining = MAX_CHARS - word.length;
  const overLimit = word.length > MAX_CHARS;
  const empty = !word.trim();

  return (
    <Shell dataState="input">
      <div style={{ padding: '28px 24px', maxWidth: 480, margin: '0 auto', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <button onClick={onBack} data-testid="one-word-input-back" style={{ alignSelf: 'flex-start', background: 'transparent', border: 'none', color: IVORY_SOFT, fontSize: 18, cursor: 'pointer' }}>‹</button>

        <div style={{ marginTop: 32, textAlign: 'center' }}>
          <p style={{ fontFamily: FONT_DISPLAY, fontSize: 26, fontStyle: 'italic', color: IVORY, lineHeight: 1.25, margin: 0 }}>
            How does tonight feel to you ✦
          </p>
          <p style={{ marginTop: 12, color: IVORY_SOFT, fontSize: 13.5, lineHeight: 1.6 }}>
            One word. One feeling. Leave it in the room.
          </p>
        </div>

        <div style={{ marginTop: 28, position: 'relative' }}>
          <input
            ref={inputRef}
            type="text" value={word}
            onChange={(e) => setWord(e.target.value)}
            placeholder="electric"
            maxLength={MAX_CHARS + 5}
            data-testid="one-word-input"
            onKeyDown={(e) => { if (e.key === 'Enter' && !empty && !overLimit) onSubmit(word.trim()); }}
            style={{
              width: '100%', boxSizing: 'border-box', textAlign: 'center',
              padding: '18px 16px', borderRadius: 8,
              background: 'rgba(245,239,223,0.06)', border: `1px solid ${HAIRLINE}`,
              fontFamily: FONT_DISPLAY, fontStyle: 'italic', fontSize: 30,
              color: IVORY, outline: 'none', letterSpacing: '-0.01em',
            }}
          />
          <p
            data-testid="one-word-counter"
            style={{ margin: '8px 0 0', textAlign: 'right', fontSize: 11, color: overLimit ? '#e0392a' : IVORY_SOFT, letterSpacing: '0.06em' }}
          >
            {word.length} / {MAX_CHARS}
          </p>
        </div>

        <div style={{ marginTop: 24 }}>
          <p style={{ margin: '0 0 10px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: GOLD, textAlign: 'center' }}>
            or tap one ✦
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => setWord(s)}
                data-testid={`one-word-suggestion-${s}`}
                style={{
                  padding: '7px 14px', borderRadius: 999,
                  background: 'rgba(245,239,223,0.06)', border: `1px solid ${HAIRLINE}`,
                  color: IVORY, fontFamily: FONT_DISPLAY, fontStyle: 'italic',
                  fontSize: 14, cursor: 'pointer',
                }}
              >{s}</button>
            ))}
          </div>
        </div>

        {error && (
          <p data-testid="one-word-error" style={{ marginTop: 16, color: '#f4b8b0', fontSize: 13, textAlign: 'center', fontStyle: 'italic' }}>{error}</p>
        )}

        <div style={{ marginTop: 'auto' }}>
          <button
            onClick={() => !empty && !overLimit && onSubmit(word.trim())}
            disabled={busy || empty || overLimit}
            data-testid="one-word-submit"
            style={{
              width: '100%', padding: '16px 18px', borderRadius: 999, border: 'none',
              cursor: (busy || empty || overLimit) ? 'default' : 'pointer',
              background: `linear-gradient(180deg, ${GOLD}, #9a7836)`,
              color: '#1a0f04', fontWeight: 700, letterSpacing: '0.04em', fontSize: 15,
              opacity: (busy || empty || overLimit) ? 0.5 : 1,
            }}
          >{busy ? 'Leaving…' : 'Leave it in the room ✦'}</button>
        </div>
      </div>
    </Shell>
  );
}

// ── 3. CONFIRMATION ─────────────────────────────────────────
function ConfirmationScreen({ onSettled }) {
  // iter210 ✦ — Personal Memory Return Path. The brief "Held"
  // beat fades into the Personal Memory Page. The legacy "See The
  // Room ✦" wall escape was removed: a single word's canonical
  // destination is the guest's own contribution page, never the
  // cinematic wall (which remains reachable from the launcher).
  useEffect(() => { const id = setTimeout(onSettled, 1500); return () => clearTimeout(id); }, [onSettled]);
  return (
    <Shell dataState="saved">
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 28 }}>
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD, margin: 0 }}>✦ Held</p>
          <p data-testid="one-word-saved" style={{ fontFamily: FONT_DISPLAY, fontSize: 32, lineHeight: 1.2, marginTop: 16, color: IVORY, fontStyle: 'italic' }}>
            Your feeling now lives inside tonight ✦
          </p>
        </div>
      </div>
    </Shell>
  );
}

// ── 4. LIVE WALL — cinematic floating typography ────────────
//
// Layout strategy:
//   • Sort visible words by weight (heaviest first).
//   • Place the top-weighted word dead center.
//   • Distribute the next 3 across the inner ring (small radius).
//   • Distribute the remaining ~14 in two concentric rings outward.
//   • Use a stable per-word hash to jitter within the ring so the
//     same room reads the same on every poll, but words don't sit
//     on a perfect circle.
function WallScreen({ wall, eventName, busy, onBack, slug }) {
  const positions = useMemo(() => layoutWords(wall.words || []), [wall.words]);

  return (
    <Shell dataState="wall">
      <style>{`
        @keyframes oneWordDrift {
          0%   { transform: translate(-50%, -50%); }
          50%  { transform: translate(calc(-50% + var(--dx, 4px)), calc(-50% + var(--dy, -5px))); }
          100% { transform: translate(-50%, -50%); }
        }
        @keyframes oneWordFadeIn {
          from { opacity: 0; }
          to   { opacity: var(--target-opacity, 0.9); }
        }
        @keyframes oneWordFreshPulse {
          0%, 100% { text-shadow: 0 0 8px rgba(193,154,75,0.0); }
          50%      { text-shadow: 0 0 18px rgba(193,154,75,0.45); }
        }
      `}</style>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <div style={{
          padding: '20px 22px 0', display: 'flex', alignItems: 'flex-start',
          justifyContent: 'space-between', position: 'relative', zIndex: 3,
        }}>
          <button onClick={onBack} data-testid="one-word-wall-back" aria-label="Back" style={{ background: 'transparent', border: 'none', color: IVORY_SOFT, fontSize: 18, cursor: 'pointer' }}>‹</button>
          <div style={{ textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD }}>✦ The Room So Far</p>
            <p data-testid="one-word-wall-count" style={{ margin: '6px 0 0', fontSize: 11, color: IVORY_SOFT }}>
              {busy && !(wall.total) ? 'Listening…' : `${wall.total || 0} ${(wall.total === 1) ? 'word' : 'words'} left tonight`}
            </p>
          </div>
          <button
            onClick={onBack}
            data-testid="one-word-add-another"
            style={{
              padding: '6px 12px', borderRadius: 999, border: `1px solid rgba(193,154,75,0.5)`,
              background: 'transparent', color: GOLD, cursor: 'pointer',
              fontSize: 10.5, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase',
            }}
          >+ Word</button>
        </div>

        {/* Take it home ✦ — guest-side download. Sits below the
            header, only shown when the wall has at least one word
            (no point taking home an empty room). */}
        {(wall.total || 0) > 0 && (
          <div
            data-testid="one-word-take-home-bar"
            style={{
              padding: '0 22px', marginTop: 4,
              display: 'flex', justifyContent: 'center', position: 'relative', zIndex: 3,
            }}
          >
            <DropOneWordDownload
              slug={slug}
              testId="dow-guest-download"
              variant="quiet"
              label="Take it home ✦"
            />
          </div>
        )}

        {/* The wall canvas — absolutely-placed words on intentional rings */}
        <div
          data-testid="one-word-wall"
          style={{ position: 'relative', flex: 1, overflow: 'hidden', marginTop: 12 }}
        >
          {positions.map(({ word, count, weight, x, y, opacity, dx, dy, delay, isFresh, fontSize }) => (
            <span
              key={word.normalized_word}
              data-testid={`one-word-tile-${word.normalized_word}`}
              data-count={count}
              data-fresh={isFresh ? '1' : '0'}
              style={{
                position: 'absolute', left: `${x}%`, top: `${y}%`,
                fontFamily: FONT_DISPLAY, fontStyle: 'italic',
                fontSize: `${fontSize}px`,
                color: isFresh ? GOLD : IVORY,
                opacity,
                '--target-opacity': opacity,
                '--dx': `${dx}px`, '--dy': `${dy}px`,
                letterSpacing: '-0.01em', whiteSpace: 'nowrap',
                transform: 'translate(-50%, -50%)',
                animation: `oneWordFadeIn 900ms ease-out ${delay}ms backwards, oneWordDrift ${10 + (weight * 1.4)}s ease-in-out infinite ${delay}ms${isFresh ? `, oneWordFreshPulse 4s ease-in-out infinite ${delay}ms` : ''}`,
                textShadow: weight > 3.2 ? `0 0 24px rgba(193,154,75,0.22)` : 'none',
                pointerEvents: 'none',
                willChange: 'transform, opacity',
              }}
            >
              {word.word}
            </span>
          ))}
          {(wall.words || []).length === 0 && !busy && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <p style={{ fontFamily: FONT_DISPLAY, fontSize: 20, fontStyle: 'italic', color: IVORY_SOFT, textAlign: 'center', padding: 28 }}>
                The room is quiet yet ✦<br />Be the first feeling.
              </p>
            </div>
          )}
        </div>
      </div>
      <PoweredFooter />
    </Shell>
  );
}

// Stable hash for placing each word — same input always lands in the
// same coords so the wall reads as one continuous atmosphere on poll.
function strHash(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i);
  return Math.abs(h);
}

/**
 * Ring layout — places at most MAX_VISIBLE_WORDS words on the wall.
 *   • 1 dominant word at center
 *   • next 3 on an inner ring (radius ~18%)
 *   • remaining (~14) split across two outer rings (radius ~32%, ~44%)
 * Words are sorted by weight first; ties broken by stable hash so the
 * same room renders the same on every poll.
 */
function layoutWords(words) {
  const visible = words.slice(0, MAX_VISIBLE_WORDS);
  if (visible.length === 0) return [];

  // Centerpiece radius scales with the word's weight so a runaway
  // dominant word pushes the inner ring further out (keeps the wall
  // legible even when one word is huge).
  const centerWeight = visible[0]?.weight || 1;
  const innerR = Math.max(24, 18 + centerWeight * 2.5); // 24–30

  const rings = [
    { count: 1, radius: 0,            fontMul: 1.0 },  // center
    { count: 3, radius: innerR,       fontMul: 0.6 },  // inner ring — pushed clear of centerpiece
    { count: 6, radius: innerR + 16,  fontMul: 0.5 },  // mid ring
    { count: 8, radius: innerR + 28,  fontMul: 0.42 }, // outer ring
  ];

  // Base font ramp: large for centerpiece, modest for outer ring.
  // weight is in [1.0, 5.0]; we scale to a max of ~52px center.
  const baseFontFor = (weight, mul) => {
    const px = 18 + weight * 6;
    return Math.max(13, Math.round(px * mul));
  };

  const placed = [];
  let idx = 0;
  rings.forEach((ring, ringIdx) => {
    const ringWords = visible.slice(idx, idx + ring.count);
    const n = ringWords.length;
    if (!n) return;
    ringWords.forEach((w, i) => {
      const h = strHash(w.normalized_word);
      // Distribute around the circle with a stable jitter so the
      // ring never reads as perfectly geometric. Offset each ring's
      // starting angle by a hash of its level so rings don't all
      // sit on the same spokes.
      const ringPhase = ringIdx * (Math.PI / 5);
      const baseAngle = n === 1 ? 0 : (i / n) * Math.PI * 2 + ringPhase;
      const jitter = ((h % 100) / 100 - 0.5) * (Math.PI / n) * 0.5;
      const angle = baseAngle + jitter;
      const radiusJitter = ((h >> 4) % 100) / 100 * 4 - 2; // ±2pp
      const radius = ring.radius + radiusJitter;
      // Squash vertically a little so mobile (taller than wide canvas) reads OK.
      const x = Math.max(10, Math.min(90, 50 + radius * Math.cos(angle)));
      const y = Math.max(12, Math.min(88, 50 + radius * Math.sin(angle) * 1.05));
      const opacity = Math.max(0.55, Math.min(0.95, 0.6 + w.weight * 0.07));
      const dx = ((h >> 8) % 8) - 4;        // small ambient drift
      const dy = ((h >> 12) % 8) - 4;
      const delay = (ringIdx * 120) + i * 80;
      placed.push({
        word: w,
        count: w.count,
        weight: w.weight,
        x, y, opacity, dx, dy, delay,
        isFresh: !!w.is_fresh,
        fontSize: baseFontFor(w.weight, ring.fontMul),
      });
    });
    idx += ring.count;
  });
  return placed;
}

// ── Page wrapper ──────────────────────────────────────────────
export default function OneWordPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  // iter210 ✦ — kiosk awareness mirrors LensNotePage. The booth-os
  // shell appends ?kiosk=1 when it launches this page so the
  // Personal Memory Page renders the "Pass to the next memory ✦"
  // gesture for the next guest.
  const [searchParams] = useSearchParams();
  const isKiosk = searchParams.get('kiosk') === '1';
  const [stage, setStage] = useState('intro');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [contributionId, setContributionId] = useState(null);
  const [wall, setWall] = useState({ total: 0, unique: 0, words: [] });

  // Live-poll the wall every 8s while it's open
  useEffect(() => {
    if (stage !== 'wall') return undefined;
    let cancelled = false;
    const fetchWall = async () => {
      try {
        const r = await axios.get(`${API}/one-word/event/${slug}/wall`);
        if (!cancelled) setWall(r.data);
      } catch { /* best effort */ }
    };
    fetchWall();
    const iv = setInterval(fetchWall, 8000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [stage, slug]);

  const submit = async (word) => {
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API}/one-word/submit`, {
        event_slug: slug,
        word,
        source: 'one_word_page',
        guest_session_token: getGuestSessionToken(),
      });
      // iter210 ✦ — capture the canonical contribution id (word_id)
      // so ConfirmationScreen can navigate to the Personal Memory
      // Page rather than the cinematic wall. Coalesced submissions
      // reuse the same word_id, so a guest tweaking their feeling
      // lands on the same Personal Memory Page.
      setContributionId(r?.data?.word_id || null);
      setStage('saved');
    } catch (e) {
      // iter184 — surface the actual failure so prod reports are
      // diagnosable in seconds (same pattern as the iter181 Memory
      // Snap fix). Server detail wins; then HTTP status; then axios
      // network-error codes; then the raw message; then the generic
      // fallback as an absolute last resort.
      const detail =
        e?.response?.data?.detail
        || (e?.response?.status ? `Server said no · code ${e.response.status}` : '')
        || (e?.code === 'ERR_NETWORK' ? 'The room lost reception · check signal ✦' : '')
        || (e?.code === 'ECONNABORTED' ? 'The room took too long ✦' : '')
        || e?.message
        || "Couldn't leave that one ✦";
      console.error('[one-word] submit failed', detail, e);
      setError(detail);
    } finally {
      setBusy(false);
    }
  };

  const goBack = () => navigate(`/event/${slug}/memory-wall`);

  // iter210 ✦ — the canonical post-submission destination. If the
  // POST returned a word_id we route to the Personal Memory Page;
  // otherwise we fall back to the cinematic wall so a successful
  // save never strands the guest on the "saved" screen.
  const settleOnPersonalMemoryPage = () => {
    if (contributionId) {
      navigate(
        `/event/${slug}/m/${contributionId}${isKiosk ? '?kiosk=1' : ''}`,
      );
    } else {
      setStage('wall');
    }
  };

  if (stage === 'intro')  return <IntroScreen onStart={() => setStage('input')} onCancel={goBack} />;
  if (stage === 'input')  return <InputScreen onSubmit={submit} onBack={() => setStage('intro')} busy={busy} error={error} />;
  if (stage === 'saved')  return <ConfirmationScreen onSettled={settleOnPersonalMemoryPage} />;
  if (stage === 'wall')   return <WallScreen wall={wall} busy={busy} onBack={() => setStage('input')} slug={slug} />;
  return null;
}
